// src/pages/VehicleDetail.jsx
import { useState, useEffect } from "react";
import { useParams, Link, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next'; 
import {
  Heart, Share2, MapPin, Fuel, Gauge, Calendar, Phone, MessageSquare,
  ChevronLeft, ChevronRight, X, CheckCircle, Star, Shield, Clock,
  Car, Zap, Settings, BarChart2, ArrowRight, Eye, Loader2
} from 'lucide-react';
import { toast } from "sonner";
import Cookies from 'js-cookie';
import { getPublicVehicleById, getPublicVehicles } from "../../../services/publicVehicle.service";
import { createLead } from "../../../services/lead.service";
import { connectSocket, getSocket } from "../../../services/socket.service";
import { useWishlist } from "../../context/WishlistContext";
import { Seo } from "../../components/Seo";

// =====================================================
// SKELETON COMPONENT
// =====================================================
const Skeleton = ({ className = "" }: { className?: string }) => (
  <div className={`animate-pulse bg-gray-200 rounded-lg ${className}`} />
);

function VehicleDetailSkeleton() {
  return (
    <div className="bg-[#F5F7FA] min-h-screen">
      <div className="max-w-7xl mx-auto px-4 py-6">
        <Skeleton className="h-4 w-72 mb-5" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-5">
            <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden">
              <Skeleton className="h-72 md:h-96 w-full rounded-none" />
              <div className="flex gap-2 p-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} className="w-16 h-12 rounded-xl" />
                ))}
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5">
              <div className="flex justify-between mb-4">
                <div>
                  <Skeleton className="h-8 w-72 mb-2" />
                  <Skeleton className="h-4 w-56" />
                </div>
                <div className="flex gap-2">
                  <Skeleton className="w-11 h-11 rounded-xl" />
                  <Skeleton className="w-11 h-11 rounded-xl" />
                </div>
              </div>
              <Skeleton className="h-8 w-40 mb-5" />
              <div className="grid grid-cols-3 gap-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-[#F5F7FA] rounded-xl p-3">
                    <Skeleton className="w-6 h-6 mb-2" />
                    <Skeleton className="h-3 w-12 mb-2" />
                    <Skeleton className="h-4 w-20" />
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden">
              <div className="flex border-b border-[#E5E7EB]">
                {[1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} className="h-12 flex-1 rounded-none" />
                ))}
              </div>
              <div className="p-5">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {[...Array(12)].map((_, i) => (
                    <div key={i} className="bg-[#F5F7FA] rounded-xl p-3">
                      <Skeleton className="h-3 w-16 mb-2" />
                      <Skeleton className="h-4 w-24" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="space-y-5">
            <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5">
              <div className="flex gap-3 mb-5">
                <Skeleton className="w-14 h-14 rounded-xl" />
                <div>
                  <Skeleton className="h-5 w-32 mb-2" />
                  <Skeleton className="h-4 w-24 mb-2" />
                  <Skeleton className="h-5 w-28 rounded-lg" />
                </div>
              </div>
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-4 w-full mb-3" />
              ))}
              <Skeleton className="h-12 w-full rounded-xl mb-3" />
              <Skeleton className="h-12 w-full rounded-xl mb-3" />
              <Skeleton className="h-12 w-full rounded-xl" />
            </div>
            <div className="rounded-2xl p-5 bg-white border border-[#E5E7EB]">
              <Skeleton className="h-6 w-40 mb-3" />
              <Skeleton className="h-4 w-full mb-2" />
              <Skeleton className="h-4 w-5/6 mb-4" />
              <Skeleton className="h-24 w-full rounded-xl mb-4" />
              <Skeleton className="h-11 w-full rounded-xl" />
            </div>
            <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5">
              <Skeleton className="h-6 w-28 mb-4" />
              <Skeleton className="h-36 w-full rounded-xl mb-3" />
              <Skeleton className="h-4 w-48" />
            </div>
          </div>
        </div>
        <div className="mt-8">
          <Skeleton className="h-7 w-44 mb-5" />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden">
                <Skeleton className="h-40 w-full rounded-none" />
                <div className="p-4">
                  <Skeleton className="h-5 w-40 mb-3" />
                  <Skeleton className="h-5 w-24 mb-2" />
                  <Skeleton className="h-4 w-20" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// =====================================================
// MAIN COMPONENT
// =====================================================
export function VehicleDetail() {
  const { i18n, t } = useTranslation('auth'); 
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { toggleWishlist, isInWishlist, canUseWishlist } = useWishlist();
  const [loading, setLoading] = useState(true);
  const [vehicle, setVehicle] = useState<any>(null);
  const [similarVehicles, setSimilarVehicles] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // UI states
  const [activeImg, setActiveImg] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [tab, setTab] = useState('specs');

  // Contact form
  const [buyerName, setBuyerName] = useState('');
  const [buyerPhone, setBuyerPhone] = useState('');
  const [buyerEmail, setBuyerEmail] = useState('');
  const [message, setMessage] = useState('');

  // ================= Auto-fill from Cookies =================
  useEffect(() => {
    try {
      const userCookie = Cookies.get('user');
      if (userCookie) {
        const user = JSON.parse(userCookie);
        if (user.firstName) {
          setBuyerName(`${user.firstName} ${user.lastName || ''}`.trim());
        }
        if (user.mobileNumber) {
          setBuyerPhone(user.mobileNumber);
        }
        if (user.email) {
          setBuyerEmail(user.email);
        }
      }
    } catch (err) {
      console.warn('Failed to parse user cookie:', err);
    }
  }, []);

  // ================= Connect Socket =================
  useEffect(() => {
    const token = localStorage.getItem('token') || Cookies.get('token');
    if (token) {
      connectSocket(token);
    }
  }, []);

  // ================= Fetch Vehicle =================
  useEffect(() => {
    const fetchVehicle = async () => {
      if (!id) return;

      try {
        setLoading(true);
        setError(null);
        setSimilarVehicles([]);

        console.log("🚗 Vehicle ID:", id);

        const refParam = searchParams.get('ref') || searchParams.get('source') || undefined;
        const response = await getPublicVehicleById(id, refParam ? { ref: refParam } : undefined);

        console.log("🚗 Vehicle API Response:", response);
        console.log("🚗 Vehicle API Data:", response?.data);
        console.log('📖 Description:', response.data.Description);

        if (response.success && response.data) {
          console.log("✅ Vehicle found:", response.data);
          console.log("👤 CustomerID:", response.data.CustomerID);

          setVehicle(response.data);
          setActiveImg(0);

          try {
            const listResponse = await getPublicVehicles({ page: 1, pageSize: 100, sortBy: 'CreatedOn', sortOrder: 'DESC' });
            const currentPrice = Number(response.data.Price) || 0;
            const rankedVehicles = (listResponse.Data || [])
              .filter((item: any) => item.ID !== response.data.ID)
              .map((item: any) => ({
                item,
                sameModel: item.ModelID === response.data.ModelID ? 1 : 0,
                sameBrand: item.BrandID === response.data.BrandID ? 1 : 0,
                priceDifference: currentPrice && Number(item.Price)
                  ? Math.abs(Number(item.Price) - currentPrice)
                  : Number.MAX_SAFE_INTEGER,
              }))
              .sort((a: any, b: any) =>
                b.sameModel - a.sameModel ||
                b.sameBrand - a.sameBrand ||
                a.priceDifference - b.priceDifference,
              )
              .slice(0, 3)
              .map(({ item }: any) => item);

            setSimilarVehicles(rankedVehicles);
          } catch {
            setSimilarVehicles([]);
          }
        } else {
          console.error("❌ Vehicle data is null:", response);
          setError(response.message || "Vehicle not found");
          toast.error(response.message || "Failed to load vehicle");
        }
      } catch (err: any) {
        console.error("❌ Vehicle fetch error:", err);
        console.error("❌ Error response:", err?.response?.data);
        setError(
          err?.response?.data?.message ||
          err?.message ||
          "Failed to load vehicle"
        );
        toast.error(
          err?.response?.data?.message ||
          err?.message ||
          "Failed to load vehicle"
        );
      } finally {
        setLoading(false);
      }
    };
    fetchVehicle();
  }, [id, i18n.language, searchParams]);

  const openContactModal = () => {
    const defaultMsg = t('contact.default_message', { title: title || 'vehicle' });
    setMessage(defaultMsg);
    setContactOpen(true);
  };

  // ================= UUID Validator =================
  const isValidUUID = (str: string) => {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex.test(str);
  };

  // ================= Lead + Message =================
  const handleLeadAndMessage = async (leadSource: string, messageText?: string) => {
    if (!id || !vehicle) return;
    if (!buyerName.trim() || !buyerPhone.trim()) {
      toast.error(t('messages.fill_details'));
      return;
    }
    const text = messageText || message;
    const sellerId = vehicle.CustomerID;

    try {
      setSubmitting(true);

      // 1. Create Lead ONLY if sellerId is valid UUID
      if (sellerId && isValidUUID(sellerId)) {
        try {
          await createLead({
            vehicleId: id,
            buyerName: buyerName.trim(),
            buyerPhone: buyerPhone.trim(),
            buyerEmail: buyerEmail.trim() || undefined,
            message: text,
            leadSource,
          });
          console.log('✅ Lead created');
        } catch (leadErr: any) {
          console.warn('Lead creation failed:', leadErr.message);
        }
      } else {
        console.warn('⚠️ Seller ID invalid – skipping lead creation');
      }

      // 2. Send real‑time message
      const socket = getSocket();
      socket.emit('sendMessage', {
        vehicleId: id,
        receiverId: sellerId,
        text,
        media: null,
      }, (response: any) => {
        if (response?.error) {
          toast.error(response.error);
        } else {
          toast.success(t('messages.lead_success'));
          setContactOpen(false);
        }
        setSubmitting(false);
      });

    } catch (err: any) {
      toast.error(err?.message || t('messages.lead_error'));
      setSubmitting(false);
    }
  };

  // ================= Loading & Error =================
  if (loading) return <VehicleDetailSkeleton />;

  if (error || !vehicle) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F7FA]">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-[#2D323A] mb-2">
            {t('vehicleDetail.not_found.title')}
          </h2>
          <p className="text-[#717182]">{error || t('vehicleDetail.not_found.description')}</p>
          <Link to="/" className="mt-4 inline-block px-6 py-3 bg-[#1E6DFF] text-white rounded-xl hover:bg-[#1557d9]">
            {t('vehicleDetail.not_found.button')}
          </Link>
        </div>
      </div>
    );
  }

  // ================= Build Data =================
  const images = vehicle.Images?.length ? vehicle.Images : ['https://via.placeholder.com/900x600?text=No+Image'];
  const title = vehicle.Title || `${vehicle.MakeName || ''} ${vehicle.ModelName || ''} ${vehicle.YearOfRegistration || ''}`.trim() || 'Vehicle';
  const location = vehicle.CityName || vehicle.AreaName || 'Location unavailable';
  const price = vehicle.Price ? `${Number(vehicle.Price).toLocaleString()} MAD` : t('vehicleDetail.similar.price_on_request');
  const seoDescription = `Buy this ${title} for ${price} in ${location}. View vehicle details, specifications, features, photos, seller information and contact options on Morrocars.`;
  const phoneNumber = String(vehicle.PhoneNumber || '').replace(/\D/g, '');
  const shareUrl = window.location.href;
  const shareText = `Check out ${title} on MORROCARS`;

  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success(t('vehicleDetail.messages.copy_success'));
    } catch {
      toast.error(t('vehicleDetail.messages.copy_error'));
    }
  };

  const shareListing = (platform: 'WhatsApp' | 'Facebook' | 'Twitter' | 'Email') => {
    let urlToShare = `${window.location.origin}/vehicle/${id}`;
    if (platform === 'WhatsApp') urlToShare += '?ref=whatsapp';
    else if (platform === 'Facebook' || platform === 'Twitter') urlToShare += '?ref=social';
    const encodedUrl = encodeURIComponent(urlToShare);
    const encodedText = encodeURIComponent(shareText);
    const shareLinks = {
      WhatsApp: `https://wa.me/?text=${encodedText}%20${encodedUrl}`,
      Facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      Twitter: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,
      Email: `mailto:?subject=${encodedText}&body=${encodedText}%0A${encodedUrl}`,
    };

    window.open(shareLinks[platform], '_blank', 'noopener,noreferrer');
  };

  const specs = [
    { label: t('vehicleDetail.specs.make'), value: vehicle.MakeName || t('vehicleDetail.specs.na') },
    { label: t('vehicleDetail.specs.model'), value: vehicle.ModelName || t('vehicleDetail.specs.na') },
    { label: t('vehicleDetail.specs.year'), value: vehicle.YearOfRegistration || t('vehicleDetail.specs.na') },
    { label: t('vehicleDetail.specs.mileage'), value: vehicle.Mileage ? `${Number(vehicle.Mileage).toLocaleString()} km` : t('vehicleDetail.specs.na') },
    { label: t('vehicleDetail.specs.fuel'), value: vehicle.FuelType || t('vehicleDetail.specs.na') },
    { label: t('vehicleDetail.specs.transmission'), value: vehicle.TransmissionTypeLabel || t('vehicleDetail.specs.na') },
    { label: t('vehicleDetail.specs.engine'), value: vehicle.EngineDisplacement ? `${vehicle.EngineDisplacement} cc` : t('vehicleDetail.specs.na') },
    { label: t('vehicleDetail.specs.power'), value: vehicle.EngineHorsePower ? `${vehicle.EngineHorsePower} HP` : t('vehicleDetail.specs.na') },
    { label: t('vehicleDetail.specs.color'), value: vehicle.ColorName || t('vehicleDetail.specs.na') },
    { label: t('vehicleDetail.specs.body_type'), value: vehicle.BodyTypeName || t('vehicleDetail.specs.na') },
    { label: t('vehicleDetail.specs.doors'), value: vehicle.NumberOfDoors || t('vehicleDetail.specs.na') },
    { label: t('vehicleDetail.specs.seats'), value: vehicle.NumberOfSeats || t('vehicleDetail.specs.na') },
    { label: t('vehicleDetail.specs.drive'), value: vehicle.DriveType || t('vehicleDetail.specs.na') },
  ].filter(s => s.value !== t('vehicleDetail.specs.na'));

  const features = Array.isArray(vehicle.Features) ? vehicle.Features : [];

  // ================= Render =================
  return (
    <>
    <Seo
  title={t('vehicleDetail.seo.title', { title })}
  description={t('vehicleDetail.seo.description', {
    title,
    price,
    location,
  })}
/>
    <div className="bg-[#F5F7FA] min-h-screen">
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Breadcrumb */}
        {/* <div className="flex items-center gap-2 text-sm text-[#717182] mb-5">
          <Link to="/" className="hover:text-[#1E6DFF]">{t('vehicleDetail.breadcrumb.home')}</Link>
          <span>/</span>
          <Link to="/search" className="hover:text-[#1E6DFF]">{t('vehicleDetail.breadcrumb.vehicles')}</Link>
          <span>/</span>
          <span className="text-[#2D323A]">{title}</span>
        </div> */}
        {/* Breadcrumb */}
        <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm text-[#717182] mb-5 flex-wrap">
          <Link to="/" className="hover:text-[#1E6DFF]">{t('vehicleDetail.breadcrumb.home')}</Link>
          <span>/</span>
          <Link to="/search" className="hover:text-[#1E6DFF]">{t('vehicleDetail.breadcrumb.vehicles')}</Link>
          {/* Make */}
          {vehicle.MakeID && vehicle.MakeName && (
            <>
              <span>/</span>
              <Link to={`/vehicles/make/${vehicle.MakeID}`} className="hover:text-[#1E6DFF] capitalize">{vehicle.MakeName}</Link>
            </>
          )}
          {/* Brand */}
          {vehicle.BrandID && vehicle.BrandName && (
            <>
              <span>/</span>
              <Link to={`/vehicles/make/${vehicle.MakeID}/brand/${vehicle.BrandID}`} className="hover:text-[#1E6DFF] capitalize">{vehicle.BrandName}</Link>
            </>
          )}
          {/* Vehicle Title (current page) */}
          <span>/</span>
          <span className="text-[#2D323A] line-clamp-1 max-w-[40ch] truncate">{title}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* LEFT COLUMN – Gallery, Title, Tabs */}
          <div className="lg:col-span-2 space-y-5">
            {/* Gallery */}
            <div className="bg-white rounded-2xl overflow-hidden border border-[#E5E7EB]">
              <div className="relative cursor-pointer" onClick={() => setLightbox(true)}>
                <img src={images[activeImg]} alt={title} className="w-full h-72 md:h-96 object-cover" />
                <button
                  onClick={e => { e.stopPropagation(); setActiveImg(i => (i - 1 + images.length) % images.length); }}
                  className="absolute left-3 top-1/2 -translate-y-1/2 p-2 bg-white/90 backdrop-blur rounded-xl shadow hover:bg-white"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={e => { e.stopPropagation(); setActiveImg(i => (i + 1) % images.length); }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-2 bg-white/90 backdrop-blur rounded-xl shadow hover:bg-white"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
                <div className="absolute bottom-3 right-3 px-2 py-1 bg-black/50 text-white text-sm rounded-lg">
                  {activeImg + 1} / {images.length}
                </div>
                {vehicle.AdTypeLabel && (
                  <div className="absolute top-3 left-3 px-2 py-1 bg-[#1E6DFF] text-white text-xs rounded-lg">
                    {vehicle.AdTypeLabel}
                  </div>
                )}
              </div>
              <div className="flex gap-2 p-3 overflow-x-auto">
                {images.map((img: string, i: number) => (
                  <button
                    key={i}
                    onClick={() => setActiveImg(i)}
                    className={`flex-shrink-0 w-16 h-12 rounded-xl overflow-hidden border-2 transition-colors ${activeImg === i ? 'border-[#1E6DFF]' : 'border-transparent'
                      }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            </div>

            {/* Title & Actions */}
            <div className="bg-white rounded-2xl p-5 border border-[#E5E7EB]">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h1 className="text-2xl text-[#2D323A] mb-1">{title}</h1>
                  <div className="flex items-center gap-3 text-sm text-[#717182]">
                    <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {location}</span>
                    <span className="flex items-center gap-1"><Eye className="w-4 h-4" /> {vehicle.ViewCount || 0} {t('vehicleDetail.actions.views')}</span>
                    <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {vehicle.CreatedOn ? new Date(vehicle.CreatedOn).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    }) : 'N/A'}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {canUseWishlist && <button
                    onClick={() => {
                      if (vehicle) {
                        toggleWishlist({
                          id: vehicle.ID || id || 1,
                          title,
                          price: vehicle.Price ? Number(vehicle.Price) : price,
                          location: vehicle.CityName || vehicle.CityID || 'Morocco',
                          year: vehicle.YearOfRegistration,
                          km: vehicle.Mileage ? Number(vehicle.Mileage) : undefined,
                          fuel: vehicle.FuelTypeName || vehicle.FuelType,
                          img: images[0]
                        });
                      }
                    }}
                    className={`p-2.5 rounded-xl border transition-colors ${isInWishlist(vehicle?.ID || id || '')
                        ? 'border-[#EF4444] bg-[#EF4444]/5 text-[#EF4444]'
                        : 'border-[#E5E7EB] text-[#717182] hover:border-[#EF4444]'
                      }`}
                    title={isInWishlist(vehicle?.ID || id || '') ? t('vehicleDetail.actions.wishlist_remove') : t('vehicleDetail.actions.wishlist_add')}
                  >
                    <Heart className={`w-5 h-5 ${isInWishlist(vehicle?.ID || id || '') ? 'fill-current' : ''}`} />
                  </button>}
                  <button onClick={() => setShareOpen(true)} className="p-2.5 rounded-xl border border-[#E5E7EB] text-[#717182] hover:border-[#1E6DFF] transition-colors">
                    <Share2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
              <p className="text-3xl text-[#1E6DFF] mb-4">{price}</p>
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-[#F5F7FA] rounded-xl p-3 flex items-center gap-2">
                  <div className="text-[#1E6DFF]"><Calendar className="w-4 h-4" /></div>
                  <div>
                    <p className="text-xs text-[#717182]">{t('vehicleDetail.specs.year')}</p>
                    <p className="text-sm text-[#2D323A]">{vehicle.YearOfRegistration || t('vehicleDetail.specs.na')}</p>
                  </div>
                </div>
                <div className="bg-[#F5F7FA] rounded-xl p-3 flex items-center gap-2">
                  <div className="text-[#1E6DFF]"><Gauge className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="text-xs text-[#717182]">{t('vehicleDetail.specs.mileage')}</p>
                    <p className="text-sm text-[#2D323A]">{vehicle.Mileage ? `${Number(vehicle.Mileage).toLocaleString()} km` : t('vehicleDetail.specs.na')}</p>
                  </div>
                </div>
                <div className="bg-[#F5F7FA] rounded-xl p-3 flex items-center gap-2">
                  <div className="text-[#1E6DFF]"><Fuel className="w-4 h-4" /></div>
                  <div>
                    <p className="text-xs text-[#717182]">{t('vehicleDetail.specs.fuel')}</p>
                    <p className="text-sm text-[#2D323A]">{vehicle.FuelType || t('vehicleDetail.specs.na')}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden">
              <div className="flex border-b border-[#E5E7EB]">
                <button
                  onClick={() => setTab('specs')}
                  className={`flex-1 py-3.5 text-sm capitalize transition-colors border-b-2 ${tab === 'specs'
                      ? 'border-[#1E6DFF] text-[#1E6DFF]'
                      : 'border-transparent text-[#717182] hover:text-[#2D323A]'
                    }`}
                >
                  {t('vehicleDetail.tabs.specs')}
                </button>
                <button
                  onClick={() => setTab('features')}
                  className={`flex-1 py-3.5 text-sm capitalize transition-colors border-b-2 ${tab === 'features'
                      ? 'border-[#1E6DFF] text-[#1E6DFF]'
                      : 'border-transparent text-[#717182] hover:text-[#2D323A]'
                    }`}
                >
                  {t('vehicleDetail.tabs.features')}
                </button>
                <button
                  onClick={() => setTab('description')}
                  className={`flex-1 py-3.5 text-sm capitalize transition-colors border-b-2 ${tab === 'description'
                      ? 'border-[#1E6DFF] text-[#1E6DFF]'
                      : 'border-transparent text-[#717182] hover:text-[#2D323A]'
                    }`}
                >
                  {t('vehicleDetail.tabs.description')}
                </button>
                <button
                  onClick={() => setTab('history')}
                  className={`flex-1 py-3.5 text-sm capitalize transition-colors border-b-2 ${tab === 'history'
                      ? 'border-[#1E6DFF] text-[#1E6DFF]'
                      : 'border-transparent text-[#717182] hover:text-[#2D323A]'
                    }`}
                >
                  {t('vehicleDetail.tabs.history')}
                </button>
              </div>
              <div className="p-5">
                {tab === 'specs' && (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {specs.map(s => (
                      <div key={s.label} className="bg-[#F5F7FA] rounded-xl p-3">
                        <p className="text-xs text-[#717182] mb-0.5">{s.label}</p>
                        <p className="text-sm text-[#2D323A]">{s.value}</p>
                      </div>
                    ))}
                  </div>
                )}
                {tab === 'features' && (
                  features.length ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {features.map((feature: { ID: string; Name: string }) => (
                        <div key={feature.ID} className="flex items-center gap-2 text-sm text-[#2D323A]">
                          <CheckCircle className="w-4 h-4 text-[#22C55E] flex-shrink-0" />
                          {feature.Name}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-[#717182]">{t('vehicleDetail.seller.no_features')}</p>
                  )
                )}
                {tab === 'description' && (
                  <div className="text-[#717182] space-y-3 text-sm leading-relaxed">
                    {vehicle.VehicleDescription ? (
                      <p>{vehicle.VehicleDescription}</p>
                    ) : (
                      <p className="text-[#717182]">{t('vehicleDetail.seller.no_description')}</p>
                    )}
                  </div>
                )}
                {tab === 'history' && (
                  <div className="text-[#717182] text-sm">
                    {vehicle.PriceHistory?.length ? (
                      <div className="space-y-3">
                        {vehicle.PriceHistory.map((entry: any) => (
                          <div key={entry.id} className="flex items-center justify-between gap-4 rounded-xl bg-[#F5F7FA] p-3">
                            <span>{new Date(entry.ChangedOn).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })}</span>
                            <span className="font-medium text-[#2D323A] text-right">
                              {Number(entry.OldPrice).toLocaleString()} MAD → {Number(entry.NewPrice).toLocaleString()} MAD
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p>{t('vehicleDetail.seller.no_history')}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN – Seller + CTAs */}
          <div className="space-y-5">
            <div className="bg-white rounded-2xl p-5 border border-[#E5E7EB]">
              <div className="flex items-center gap-3 mb-4">
                {vehicle.Seller?.ProfilePhoto ? (
                  <img src={vehicle.Seller.ProfilePhoto} alt={vehicle.Seller.Name || 'Seller'} className="w-14 h-14 rounded-xl object-cover" />
                ) : (
                  <div className="w-14 h-14 bg-[#1E6DFF] rounded-xl flex items-center justify-center text-white text-xl">
                    {vehicle.Seller?.Name?.[0] || 'S'}
                  </div>
                )}
                <div>
                  <p className="text-[#2D323A]">{vehicle.Seller?.Name || t('vehicleDetail.seller.private_seller')}</p>
                  <div className="flex items-center gap-1 text-sm text-[#F59E0B]">
                    <Star className="w-4 h-4 fill-current" />
                    <span>4.9</span>
                    <span className="text-[#717182]">(128 {t('vehicleDetail.seller.reviews')})</span>
                  </div>
                  <span className="inline-block mt-1 text-xs px-2 py-0.5 bg-[#1E6DFF]/10 text-[#1E6DFF] rounded-lg">
                    {vehicle.SellerTypeLabel || t('vehicleDetail.seller.private_seller')}
                  </span>
                </div>
              </div>
              <div className="space-y-2 text-sm text-[#717182] mb-4">
                {vehicle.Seller?.IsVerifiedDealer === true && (
                  <p className="flex items-center gap-2"><Shield className="w-4 h-4 text-[#22C55E]" />{t('vehicleDetail.seller.verified')}</p>
                )}
                <p className="flex items-center gap-2"><Car className="w-4 h-4 text-[#1E6DFF]" /> {vehicle.Seller?.ActiveListingCount ?? 0} {t('vehicleDetail.seller.active_listings')}</p>
                <p className="flex items-center gap-2"><Clock className="w-4 h-4 text-[#717182]" /> {t('vehicleDetail.seller.responds')}</p>
                <p className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#717182]" /> {vehicle.Seller?.City || location}</p>
              </div>
              {vehicle.ShowPhone === 1 && phoneNumber && (
                <a href={`tel:${phoneNumber}`} className="w-full py-3 bg-[#1E6DFF] text-white rounded-xl hover:bg-[#1557d9] transition-colors shadow-md flex items-center justify-center gap-2 mb-2">
                  <Phone className="w-5 h-5" /> {t('vehicleDetail.seller.call')}
                </a>
              )}
              {vehicle.ShowMessages === 1 && (
                <button onClick={() => setContactOpen(true)} className="w-full py-3 border border-[#E5E7EB] text-[#2D323A] rounded-xl hover:bg-[#F5F7FA] transition-colors flex items-center justify-center gap-2 mb-2">
                  <MessageSquare className="w-5 h-5" /> {t('vehicleDetail.seller.message')}
                </button>
              )}
              {vehicle.ShowWhatsApp === 1 && phoneNumber && (
                <a href={`https://wa.me/${phoneNumber}?text=${encodeURIComponent(`Hello, I'm interested in your ${title}. Is it still available?`)}`} target="_blank" rel="noreferrer" className="w-full py-3 bg-[#25D366] text-white rounded-xl hover:bg-[#20bd5a] transition-colors flex items-center justify-center gap-2">
                  <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" /></svg>
                  {t('vehicleDetail.seller.whatsapp')}
                </a>
              )}
            </div>

            {/* Financing CTA */}
            {['Yes', 'Bank only'].includes(vehicle.FinancingAvailable) && (
              <div className="bg-gradient-to-br from-[#1E6DFF] to-[#1557d9] rounded-2xl p-5 text-white">
                <div className="flex items-center gap-2 mb-2">
                  <Zap className="w-5 h-5" />
                  <span>{t('vehicleDetail.financing.title')}</span>
                </div>
                <p className="text-white/80 text-sm mb-4">{t('vehicleDetail.financing.description')}</p>
                <div className="bg-white/20 backdrop-blur rounded-xl p-3 mb-4">
                  <p className="text-sm text-white/80 mb-1">{t('vehicleDetail.financing.monthly')}</p>
                  <p className="text-2xl">~4,200 MAD/mo</p>
                  <p className="text-xs text-white/60">{t('vehicleDetail.financing.based_on')}</p>
                </div>
                <button className="w-full py-2.5 bg-white text-[#1E6DFF] rounded-xl hover:bg-white/90 transition-colors flex items-center justify-center gap-2">
                  {t('vehicleDetail.financing.button')} <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Location */}
            <div className="bg-white rounded-2xl p-5 border border-[#E5E7EB]">
              <h3 className="text-[#2D323A] mb-3 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-[#1E6DFF]" /> {t('vehicleDetail.location.title')}
              </h3>
              <iframe
                title={`Map of ${location}`}
                src={`https://www.google.com/maps?q=${encodeURIComponent(location)}&output=embed`}
                className="mb-2 h-36 w-full rounded-xl border-0"
                loading="lazy"
              />
              <p className="text-sm text-[#717182]">{location}</p>
            </div>
          </div>
        </div>

        {/* Similar Vehicles */}
        <div className="mt-8">
          <h2 className="text-[#2D323A] mb-5">{t('vehicleDetail.similar.title')}</h2>
          {similarVehicles.length ? (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {similarVehicles.map((similarVehicle) => {
                const similarTitle = similarVehicle.Title || `${similarVehicle.MakeName || ''} ${similarVehicle.ModelName || ''}`.trim() || 'Vehicle';
                const similarImage = similarVehicle.Images?.[0];
                return (
                  <Link key={similarVehicle.ID} to={`/vehicle/${similarVehicle.ID}`} className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden hover:shadow-md transition-shadow">
                    {similarImage ? (
                      <img src={similarImage} alt={similarTitle} className="h-40 w-full object-cover" />
                    ) : (
                      <div className="h-40 bg-[#F5F7FA] flex items-center justify-center text-sm text-[#717182]">
                        {t('vehicleDetail.similar.no_image')}
                      </div>
                    )}
                    <div className="p-4">
                      <p className="text-[#2D323A] truncate">{similarTitle}</p>
                      <p className="mt-1 text-lg text-[#1E6DFF]">{similarVehicle.Price ? `${Number(similarVehicle.Price).toLocaleString()} MAD` : t('vehicleDetail.similar.price_on_request')}</p>
                      <p className="mt-1 text-sm text-[#717182]">{similarVehicle.Mileage ? `${Number(similarVehicle.Mileage).toLocaleString()} km` : t('vehicleDetail.similar.mileage_unavailable')}</p>
                    </div>
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className="rounded-2xl border border-[#E5E7EB] bg-white p-6 text-sm text-[#717182]">
              {t('vehicleDetail.similar.no_similar')}
            </div>
          )}
        </div>
      </div>

      {/* Lightbox Modal */}
      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center" onClick={() => setLightbox(false)}>
          <button className="absolute top-4 right-4 p-2 text-white hover:bg-white/10 rounded-xl" onClick={() => setLightbox(false)}>
            <X className="w-7 h-7" />
          </button>
          <button className="absolute left-4 top-1/2 -translate-y-1/2 p-2 text-white hover:bg-white/10 rounded-xl" onClick={e => { e.stopPropagation(); setActiveImg(i => (i - 1 + images.length) % images.length); }}>
            <ChevronLeft className="w-7 h-7" />
          </button>
          <img src={images[activeImg]} alt="" className="max-w-4xl max-h-[85vh] w-full object-contain" onClick={e => e.stopPropagation()} />
          <button className="absolute right-4 top-1/2 -translate-y-1/2 p-2 text-white hover:bg-white/10 rounded-xl" onClick={e => { e.stopPropagation(); setActiveImg(i => (i + 1) % images.length); }}>
            <ChevronRight className="w-7 h-7" />
          </button>
          <div className="absolute bottom-4 flex gap-2">
            {images.map((_: any, i: number) => (
              <button key={i} onClick={e => { e.stopPropagation(); setActiveImg(i); }} className={`w-2.5 h-2.5 rounded-full transition-colors ${i === activeImg ? 'bg-white' : 'bg-white/40'}`} />
            ))}
          </div>
        </div>
      )}

      {/* Contact Modal */}
      {contactOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setContactOpen(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-[#2D323A]">{t('vehicleDetail.contact.title')}</h3>
              <button onClick={() => setContactOpen(false)}><X className="w-5 h-5 text-[#717182]" /></button>
            </div>
            <div className="space-y-3">
              <input
                placeholder={t('vehicleDetail.contact.name')}
                value={buyerName}
                onChange={e => setBuyerName(e.target.value)}
                className="w-full px-4 py-3 bg-[#F5F7FA] border border-[#E5E7EB] rounded-xl focus:outline-none focus:border-[#1E6DFF]"
              />
              <input
                placeholder={t('vehicleDetail.contact.phone')}
                value={buyerPhone}
                onChange={e => setBuyerPhone(e.target.value)}
                className="w-full px-4 py-3 bg-[#F5F7FA] border border-[#E5E7EB] rounded-xl focus:outline-none focus:border-[#1E6DFF]"
              />
              <input
                placeholder={t('vehicleDetail.contact.email')}
                value={buyerEmail}
                onChange={e => setBuyerEmail(e.target.value)}
                className="w-full px-4 py-3 bg-[#F5F7FA] border border-[#E5E7EB] rounded-xl focus:outline-none focus:border-[#1E6DFF]"
              />
              <textarea
                placeholder={t('vehicleDetail.contact.message')}
                rows={4}
                value={message}
                onChange={e => setMessage(e.target.value)}
                className="w-full px-4 py-3 bg-[#F5F7FA] border border-[#E5E7EB] rounded-xl focus:outline-none focus:border-[#1E6DFF] resize-none"
              />
              <button
                onClick={() => handleLeadAndMessage('Message', message)}
                disabled={submitting}
                className="w-full py-3 bg-[#1E6DFF] text-white rounded-xl hover:bg-[#1557d9] transition-colors shadow-md flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <MessageSquare className="w-5 h-5" />}
                {submitting ? t('vehicleDetail.contact.sending') : t('vehicleDetail.contact.send')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {shareOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShareOpen(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-[#2D323A]">{t('vehicleDetail.share.title')}</h3>
              <button onClick={() => setShareOpen(false)}><X className="w-5 h-5 text-[#717182]" /></button>
            </div>
            <div className="bg-[#F5F7FA] rounded-xl p-3 flex items-center gap-2 mb-4">
              <input value={shareUrl} readOnly className="flex-1 bg-transparent text-sm text-[#717182] focus:outline-none" />
              <button onClick={copyShareLink} className="text-[#1E6DFF] text-sm hover:underline">{t('vehicleDetail.share.copy')}</button>
            </div>
            <div className="grid grid-cols-4 gap-3">
              <button onClick={() => shareListing('WhatsApp')} className="flex flex-col items-center gap-1 p-3 bg-[#F5F7FA] rounded-xl hover:bg-[#E5E7EB] transition-colors">
                <span className="text-lg">💬</span>
                <span className="text-xs text-[#717182]">{t('vehicleDetail.share.whatsapp')}</span>
              </button>
              <button onClick={() => shareListing('Facebook')} className="flex flex-col items-center gap-1 p-3 bg-[#F5F7FA] rounded-xl hover:bg-[#E5E7EB] transition-colors">
                <span className="text-lg">📘</span>
                <span className="text-xs text-[#717182]">{t('vehicleDetail.share.facebook')}</span>
              </button>
              <button onClick={() => shareListing('Twitter')} className="flex flex-col items-center gap-1 p-3 bg-[#F5F7FA] rounded-xl hover:bg-[#E5E7EB] transition-colors">
                <span className="text-lg">🐦</span>
                <span className="text-xs text-[#717182]">{t('vehicleDetail.share.twitter')}</span>
              </button>
              <button onClick={() => shareListing('Email')} className="flex flex-col items-center gap-1 p-3 bg-[#F5F7FA] rounded-xl hover:bg-[#E5E7EB] transition-colors">
                <span className="text-lg">📧</span>
                <span className="text-xs text-[#717182]">{t('vehicleDetail.share.email')}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
}
